import React, { useEffect, useState, useRef, useReducer } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { i18nIntl, getPromotionMainColor, imgPath, validateURL, isNotEmpty, getFileSize, base64ToBytes, isColorBright, CamelCaseText, adaptHeightOnWidget } from '../../lib/ep.js';
import { SortableContainer, SortableElement, SortableHandle, arrayMove } from 'react-sortable-hoc';

import ActionLink from '../builder/ActionLink';
import ActionButton, { SecondaryButton, SquaredButton } from '../builder/ActionButton';
import DisabledArea from '../builder/DisabledArea';
import DropzoneUploader from '../builder/DropzoneUploader';
import { DynamicForm } from '../builder/DynamicForm.js';
import FieldLabel from '../builder/FieldLabel.js';
import Input from '../builder/Input';
import InputWithButton from '../builder/InputWithButton';
import SaveButton from '../builder/SaveButton';
import TextArea from '../builder/TextArea';

import FloatingLayerPublic from '../front/FloatingLayerPublic';
import SingleGallery from '../front/SingleGallery';
import { useCSS } from '../../lib/hooks.js';
import LeveledHTMLText from './helpers/LeveledHTMLText.js';
import ButtonCollection from '../builder/ButtonCollection.js';
import SectionTitle from '../builder/SectionTitle.js';
import SectionSubtitle from '../builder/SectionSubtitle.js';
import Tooltip from '../builder/Tooltip.js';
import FloatingLayer from '../builder/FloatingLayer.js';
import { Step0 } from '../backend/MediaBuilder.js';
import InstagramMediaSelector from './InstagramMediaSelector.js';
import TikTokMediaSelector from './TikTokMediaSelector.js';
import { localizeFields } from '../builder/DynamicFields.js';
import ToggleCollection from '../builder/ToggleCollection.js';

const default_thumb_w = 180;
const uploader_ui_color = '#FFFFFF';
const error_ui_color = '#CC0000';
const uploader_ui_border_color = '#000000';

const Wrapper = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const is_edit = props?.is_edit ?? false;
	const has_medias = props?.has_medias ?? false;
	const is_add_builder = props?.is_add_builder ?? false;
	const step = props?.step ?? 1;
	const media_has_step_0 = props?.media_has_step_0 ?? false;

	if(is_edit) {
		// as displayed in MultimediaTool:
		const disabled_submit_step_1 = props?.disabled_submit_step_1 ?? false;
		const disabled_submit_step_2 = props?.disabled_submit_step_2 ?? false;
		const participation = props?.participation ?? false;

		return (
			<div>
				<SectionTitle>Participation edit</SectionTitle>
				<SectionSubtitle autotranslate={false}><FormattedMessage id="{name} on {created}" values={{ name:<strong>{participation.name}</strong>, created:<strong>{EPDateFormat(participation.created, 'LL, H:mm')}</strong> }} /></SectionSubtitle>

				{props.children}

				<ButtonCollection in_floating={true}>
					{(step == 1) && (
						<SaveButton
							onClick={(e, cb) => props.goToStep(2, cb)}
							disabled={(disabled_submit_step_1) ? true : false}
						>Continue</SaveButton>
					)}

					{(step == 2) && (
						<SaveButton
							onClick={(e, cb) => props.submitMedias(cb)}
							disabled={(disabled_submit_step_2) ? true : false}
						>Save changes</SaveButton>
					)}

					<SecondaryButton
						onClick={() => props.onCloseLayer()}
					>Close</SecondaryButton>
				</ButtonCollection>
			</div>
		);
	}

	if(props.layer_actions != undefined) {
		const disabled_submit_step_1 = props?.disabled_submit_step_1 ?? false;

		// as displayed in in-builder CandidateEditor:
		return (
			<>
				{props.children}

				<ButtonCollection in_floating={true} _class={{ other:'gui_flex_space_between' }}>
					<SaveButton
						icon={(!has_medias) ? 'knk-alert' : null}
						onClick={(has_medias) ? (e, cb) => props.submitMedias(cb) : () => { return false; }}
						disabled={disabled_submit_step_1 || !has_medias}
						tooltip={(!has_medias) ? <Tooltip with_icon={false} position='top'>Please insert at least the required medias</Tooltip> : null}
					>Save changes</SaveButton>
					
					{props.layer_actions}
				</ButtonCollection>
			</>
		);
	}

	const main_image = props?.main_image ?? false;
	const main_video = props?.main_video ?? false;
	const page_introduction = props?.page_introduction ?? false;

	return (
		<div class="flex_cols">
			{(main_image || main_video) && (
				<div class="flex_col col_1">
					<div class="main_media_flex">
						{(main_image) && (
							<img src={main_image} alt="" style={{maxWidth:'100%'}} />
						)}

						{(main_video) && (
							<VideoEmbed video_url={main_video.full_url} video_thumb={main_video.image} />
						)}
					</div>
				</div>
			)}

			<div class="flex_col col_2">
				<div class="gui_stepped_indicator original_color_item">
					<ul>
						{(media_has_step_0) && (
							<li
								class={'hb_mainFontWeightBold' + ((step == 0) ? ' selected' : '')}
								dangerouslySetInnerHTML={{ __html:i18n("Basic<br />information") }}
							/>
						)}

						<li
							class={'hb_mainFontWeightBold' + ((step == 1) ? ' selected' : '')}
							dangerouslySetInnerHTML={{ __html:i18n("Upload<br />content") }}
						/>

						<li
							class={'hb_mainFontWeightBold' + ((step == 2) ? ' selected' : '')}
							dangerouslySetInnerHTML={{ __html:i18n("Preview<br />and submit") }}
						/>
					</ul>
				</div>

				<div class="register_form_flex multimedia_form" style={{width:'100%'}}>
					{(!is_add_builder && step != 2) && (
						<LeveledHTMLText
							_class={{ other:'gui_main_text_color_on_bg' }}
						>{page_introduction}</LeveledHTMLText>
					)}

					{props.children}
				</div>
			</div>
		</div>
	)
};

const CombinedMediaUploader = ({ stage, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const media_origins_arr = props?.media_origins_arr ?? false;
	if(media_origins_arr == false || media_origins_arr.length < 1) return null;
	
	const is_builder = props?.is_builder ?? false;
	const is_edit_builder = props?.is_edit_builder ?? false;
	const is_add_builder = props?.is_add_builder ?? false;
	const accepted_types = props?.accepted_types ?? false;
	const disabled_add_media = props?.disabled_add_media ?? false;
	const error_num_files = props?.error_num_files ?? false;
	const errors = props?.errors || {};
	const total_medias = props?.total_medias ?? 0;
	const change = props?.change ?? false;
	const model = props?.model ?? false;

	const max_medias = stage?.media_max_elements ?? 1;

	//Comprovem que no sigui un retorn d'instagram logat i haguem de posar aquesta per defecte
	const params = new URLSearchParams(new URL(window.location.href).search);
	const social_param_instagram = params.get('social') && params.get('social') == 'instagram';
	const social_param_tiktok = params.get('social') && params.get('social') == 'tiktok';
	const sid_param = params.get('sid');
	
	let default_origin_type = media_origins_arr[0].type;
	let default_origin_upload_type = media_origins_arr[0].upload_type;

	if(social_param_instagram && sid_param){
		default_origin_type = default_origin_upload_type = 'instagram';
	}else if(social_param_tiktok && sid_param){
		default_origin_type = default_origin_upload_type = 'tiktok';
	}

	const [ origin_selected, setOriginSelected ] = useState(default_origin_type);
	const [ upload_type_selected, setUploadTypeSelected ] = useState(default_origin_upload_type);

	const acceptedFiles_array = [];
	if(stage.media_image) {
		acceptedFiles_array.push(['image/jpeg','image/png','image/bmp','image/tiff','image/x-icon']);
	}
	if(stage.media_video) {
		acceptedFiles_array.push(['video/x-ms-asf', 'video/x-ms-wmv', 'video/x-ms-wmx', 'video/x-ms-wm', 'video/avi', 'video/divx', 'video/x-flv', 'video/quicktime', 'video/mpeg', 'video/mp4', 'video/ogg', 'video/webm', 'video/x-matroska','video/3gpp']);
	}
	if(stage.media_audio) acceptedFiles_array.push('audio/*');
	if(stage.media_pdf) acceptedFiles_array.push('application/pdf');

	const ReactDOMServer = require('react-dom/server');

	const properties_upload = {
		djsConfig:{
			addRemoveLinks:false,
			autoProcessQueue:true,
			parallelUploads:1,
			createImageThumbnails:true,
			maxFilesize:stage?.media_size ?? 1,	//MB
			paramName:'media',
			timeout:30000,
			maxThumbnailFilesize:20,
			maxFiles:max_medias,
			//parallelUploads:(stage.media_max_elements != undefined) ? stage.media_max_elements : 1,
			acceptedFiles:acceptedFiles_array.join(','),
			url:`/stages/upload/${stage.id}/`,
			thumbnailWidth:default_thumb_w * 2,
			thumbnailHeight:null,
			thumbnailMethod:'contain',
			previewTemplate:ReactDOMServer.renderToStaticMarkup(<div></div>)
		},
		componentConfig:{
			//iconFiletypes: ['.jpg', '.png', '.gif'],
			//showFiletypeIcon: false,
			postUrl: `/stages/upload/${stage.id}/`,
		},
		eventHandlers:{
			init:(dropzone) => {
				props.initDropzone(dropzone);
			},

			sending:(file, xhr, formData) => {
				xhr.open('POST', `/stages/upload/${stage.id}/uploadSize_${getFileSize(file.size,0).replace(' ','')}`, true);
				props.fileAdded(file,xhr,formData);
			},

			addedfile:(file) => {
				props.setErrors({});
			},

			removedfile:(file) => {
			},

			uploadprogress:(file) => {
				if(!file.skip) props.uploadprogress(file);
			},

			thumbnail:(file, thumb) => {
				// props.setMedias((m) => {
				// 	for(var i in m) {
				// 		if(m[i].index != file.index) continue;

				// 		if(m[i].height == undefined) m[i].height = file?.height || default_thumb_w;
				// 		if(m[i].width == undefined) m[i].width = file?.width || default_thumb_w;
				// 		if(m[i].thumbnail == undefined) m[i].thumbnail = thumb;
				// 		console.log('final medias',m);
				// 		return [...m];
				// 	}

				// 	return m;
				// });
			},

			error:(d) => {
				if(d.size > (stage?.media_size ?? 1) * 1024 * 1024) {
					props.setErrors({
						media:props.intl.formatMessage({ id:"You have exceeded the maximum file size{max}" }, { max:` ${stage?.media_size}MB`}),
					});
				}
			},

			complete:(file) => {
				if(!file.skip) props.completedFile(file);
			},

			addedfiles:(files) => {
			},
		},
	};

	const changeOrigin = (item) => {
		setOriginSelected(item.type);
		setUploadTypeSelected(item.upload_type);
		if(change)change('youtube', '');
		props.validate();
		let errors_component = { ...errors };
		if(errors_component.youtube != undefined) delete errors_component.youtube;
		props.setErrors({ ...errors_component });
	};

	let has_type_upload = media_origins_arr.find((obj) => (obj.upload_type == 'upload'));
	if(has_type_upload == undefined) has_type_upload = false;

	let has_type_link = media_origins_arr.find((obj) => (obj.upload_type == 'link'));
	if(has_type_link == undefined) has_type_link = false;

	let has_type_text = media_origins_arr.find((obj) => (obj.upload_type == 'text'));
	if(has_type_text == undefined) has_type_text = false;

	let has_type_url = media_origins_arr.find((obj) => (obj.upload_type == 'url'));
	if(has_type_url == undefined) has_type_url = false;
	
	let has_type_tt = media_origins_arr.find((obj) => (obj.upload_type == 'tiktok'));
	if(has_type_tt == undefined) has_type_tt = false;
	
	useEffect(() => {
		if(is_builder) {
			setOriginSelected(media_origins_arr[0].type);
			setUploadTypeSelected(media_origins_arr[0].upload_type);
		}
	}, [ media_origins_arr ]);

	let upload_msg = "";
	switch(origin_selected) {
		case 'image':
		default:
			upload_msg = i18n("Select image");
			break;

		case 'video':
			upload_msg = i18n("Select video");
			break;

		case 'pdf':
			upload_msg = i18n("Select PDF");
			break;
	}
	upload_msg += `<span class="gui_not_in_start_tiny">(${i18n("maximum", 'ucfirst')}: ${stage.media_size} MB)</span>`;

	const enable_network_connect = (!is_add_builder) ? true : false;

	const formats_field_classes = ['input', 'in_top', 'combo_media', 'width_100'];
	if(is_edit_builder || is_add_builder) formats_field_classes.push('in_layer');

	return (
		<div class="react_field media_type_selector">
			<div class={formats_field_classes.join(" ")}>
				{(media_origins_arr.length > 1) && (
					<div class="this_combo_media">
						<FieldLabel>Select the format</FieldLabel>

						<div class="origin_picker">
							{media_origins_arr.map((origin, o) => {
								const selected_icon_color = (props.is_main_color_bg_light != undefined && props.is_main_color_bg_light) ? 'blackIcon' : 'whiteIcon';
								const selected = (origin_selected == origin.type) ? `selected ${selected_icon_color}` : null;
								return (
									<SquaredButton
										key={`media_origin_${o}`}
										color={false}
										icon={origin.icon}
										_class={{ other:selected }}
										onClick={() => changeOrigin(origin)}
										aria-label="Change origin"
									/>
								)
							})}
						</div>
					</div>
				)}

				{(media_origins_arr.length > 1 || max_medias > 0 && props.medias.length<max_medias) && (
					<div class={`combo_contents is_${upload_type_selected}`}>
						{(has_type_upload && upload_type_selected == 'upload') && (
							<DisabledArea disabled={disabled_add_media} message={error_num_files}>
								<DropzoneUploader
									key={`test_${total_medias}`}
									{...properties_upload}
									errors={errors}
									name="media"
									dd_size="full_w"
									display_custom_dragging_instructions={upload_msg}
									_accepted_formats={accepted_types}
									max_filesize={`${stage.media_size} MB`}
									display_extra_info={false}
									autotranslate={false}
								/>
							</DisabledArea>
						)}

						{(has_type_link && upload_type_selected == 'link') && (
							<DisabledArea disabled={disabled_add_media} message={error_num_files}>
								<InputWithButton
									disabled={disabled_add_media}
									_class={{ first:true, input_size:'big' }}
									label_props={{
										_class:{ other:'in_bottom' },
									}}
									errors={errors}
									submitColor={false}
									submitIcon='knk-add-item'
									change={change}
									model={model}
									name="youtube"
									placeholder={i18n("Insert the YouTube URL")}
									onSubmit={(value, cb) => props.addYoutube(cb,value)}
								/>
							</DisabledArea>
						)}

						{(has_type_url && upload_type_selected == 'url') && (
							<DisabledArea disabled={disabled_add_media} message={error_num_files}>
								<InputWithButton
									disabled={disabled_add_media}
									_class={{ first:true, input_size:'big' }}
									label_props={{
										_class:{ other:'in_bottom' },
									}}
									errors={errors}
									submitColor={false}
									submitIcon='knk-add-item'
									change={change}
									model={model}
									name="url"
									placeholder={i18n("Insert the URL")}
									onSubmit={(e, cb) => props.addLink(cb)}
								/>
							</DisabledArea>
						)}
						
						{(has_type_tt && upload_type_selected == 'tiktok' && enable_network_connect) && (
							<DisabledArea disabled={disabled_add_media} message={error_num_files}>
								<TikTokMediaSelector
									promotion={props.promotion}
									stage={stage}
									max_elements={props.pending_files}
									onSelect={(medias) => props.addFromImgUrl(medias, 'tiktok')}
									user={props.user ?? {}}
									medias={props.medias ?? {}}
									callback={props.callback}
									disabled={is_builder}
									intl={props.intl}
								/>
							</DisabledArea>
						)}

						{(!enable_network_connect && (has_type_tt && upload_type_selected == 'tiktok')) && (
							<p>
								<FormattedMessage
									id="If you need to add {type} uploaded by users to their {network} profile, you should go to the {link}, where you can enter the URL of the post to import it into the contest."
									values={{
										type:i18n("videos"),
										network:CamelCaseText(upload_type_selected),
										link:<ActionLink
											_href={`/users/audience/${props.promotion.id}/gallery/${stage.id}`}
											autotranslate={false}
											_target='_blank'
											display_open_in_new_tab={true}
										>{`${i18n("Users")} > ${i18n("Multimedia section")}`}</ActionLink>,
									}}
								/>
							</p>
						)}

						{(has_type_text && upload_type_selected == 'text') && (
							<DisabledArea disabled={disabled_add_media} message={error_num_files}>
								<div>
									<p>
										{i18n("Do you want to add text to your entry?")}&nbsp;
										<ActionButton
											disabled={disabled_add_media}
											_fullclass="gui_inline"
											onClick={(e, cb) => props.addText(cb)}
										>Add text box</ActionButton></p>
								</div>
							</DisabledArea>
						)}
					</div>
				)}
			</div>
		</div>
	)
};

const DragHandle = SortableHandle(({ height, ...props }) => {
	return (
		<div class="entry_sorter_target" style={{height:`${height}px`}}>
			<span class="dragging_icon knk-sorter-target-v" />
		</div>
	)
});

const reducer = (state, action) => {
	switch(action.type) {
		case 'CHANGE':
			return { ...state, [action.k]:action.v };
	}
	return state;
}

const SortableFields = SortableContainer(({ items, data, ...props }) => {
	return (
		<ul class="gui_medias_to_upload">
			{items.map((item, index) => {
				return (
					<SortableItem
						key={`media_${index}`}
						index={index}
						item={item}
						pos={index}
						intl={props.intl}
						errors={props.errors}
						num_items={items.length}
						{...data}
					/>
				)
			})}
		</ul>
	)
});

const SortableItem = SortableElement(({ errors, stage, index, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const media = props.item;
	const is_edit = props?.is_edit ?? false;
	const is_edit_builder = props?.is_edit_builder ?? false;
	const is_add_builder = props?.is_add_builder ?? false;
	const pos_index = props?.pos ?? 0;
	const with_dragger = (props.num_items != undefined && props.num_items > 1) ? true : false;

	const error_media = (errors[media.index] != undefined) ? { caption:errors[media.index] } : {};
	if(media.status == 'skip') return null;
	if(props.is_debug && pos_index >= stage.media_max_elements) return null;

	const is_builder = props?.is_builder ?? false;
	const is_drag_enabled = props?.is_drag_enabled ?? false;

	// pels vídeos / audios, passar la imatge que toqui
	const media_thumbnail = media.thumbnail;
	const media_w = media?.twidth || (media?.width || default_thumb_w);
	const media_h = media?.theight || (media?.height || default_thumb_w);
	const media_p = media?.progress ?? 0;
	const thumb_proportion = media_h / media_w;
	const thumb_w = default_thumb_w;
	const thumb_h = Math.round(thumb_w * thumb_proportion);

	const thumb_progress = Math.min(100, Math.max(0, media_p)) / 100;
	const loading_x = thumb_w - thumb_w * (1 - thumb_progress);

	const humanize_filesize = (size) => {
		if(size > 1048576) return (Math.ceil(100 * size / 1048576) / 100) + " MB";
		if(size > 1024) return Math.ceil(size / 1024) + " KB";
	};

	const is_image = (media.type == 'image') ? true : false;
	const is_pdf = (media.type == 'pdf') ? true : false;
	const is_text = (media.type == 'text') ? true : false;
	const is_url = (media.type == 'url') ? true : false;
	const is_video = (media.type == 'video') ? true : false;
	const file_completed_not_pdf = (media.status == 'completed') ? true : false;
	const file_completed_pdf = (media.cover_status == 'completed') ? true : false;

	let label_status = false;
	if(media.status == 'failed') {
		label_status = i18n("Error");
	} else {
		if(is_pdf && !file_completed_pdf && file_completed_not_pdf) {
			label_status = i18n("Uploading thumbs");
		} else if(media_p > 99) {
			label_status = (is_pdf) ? i18n("Processing pdf file") : i18n("Processing image");
			label_status = (is_video) ? i18n("Processing video") : label_status;
		} else {
			label_status = `${i18n("Uploading")}: ${humanize_filesize(media.size)}`;
		}
	}

	const deleteWithConfirm = () => {
		if(media.text == '') {
			props.deleteFile(media);
		} else {
			alert(i18n("You must first delete the text in order to remove the box. Thanks!"));
		}
	};

	if(is_text) {
		const is_mandatory = (pos_index < parseInt(stage.media_min_elements)) ? true : false;
		const enable_delete = (parseInt(stage.media_min_elements) > 1 || is_edit || is_edit_builder || is_add_builder) ? true : false;
		const min_length = stage?.media_text_min ?? 0;
		const max_length = stage?.media_text_max ?? 1500;
		return (
			<li class={`uploading_media_block is_media is_draggable_item awesome is_${media.status}`}>
				{(with_dragger) && (
					<DragHandle />
				)}

				<div class="media_info">
					<div class="media_caption">
						<TextArea
							_class={{first:true, v_size:'big'}}
							label_props={{
								mandatory:is_mandatory,
								_extra_component:(enable_delete) ? <ActionLink onClick={deleteWithConfirm}>Delete</ActionLink> : null,
							}}
							disabled={is_builder}
							errors={error_media}
							name="text"
							change={(k, v) => props.changeMedia('text', media.index, v)}
							value={media.text}
							min_length={min_length > 0 ? min_length : null}
							max_length={max_length}
						>Write your text</TextArea>
					</div>
				</div>
			</li>
		)
	}

	if(is_url) {
		const is_mandatory = pos_index < parseInt(stage.media_min_elements) ? true : false;

		return (
			<li class={`uploading_media_block is_media is_draggable_item awesome is_${media.status}`}>
				{(with_dragger) && (
					<DragHandle />
				)}

				<div class="media_info">
					<div class="media_caption">
						<Input
							_class={{first:true}}
							label_props={{
								mandatory:is_mandatory,
								_extra_component:<ActionLink onClick={() => props.deleteFile(media)}>Delete</ActionLink>,
							}}
							disabled={(is_builder || validateURL(media.url) && media.title != '')}
							errors={error_media}
							name="url"
							change={(k, v) => props.changeMedia('url', media.index, v)}
							value={media.url}
						>Link</Input>
						
						<Input
							label_props={{
								mandatory:true,
							}}
							disabled={(is_builder || !validateURL(media.url))}
							errors={error_media}
							name="title"
							change={(k, v) => props.changeMedia('title', media.index, v)}
							value={media.title}
						>Link title</Input>

						<TextArea
							label_props={{
							}}
							disabled={(is_builder || !validateURL(media.url))}
							errors={error_media}
							name="description"
							change={(k, v) => props.changeMedia('description', media.index, v)}
							value={media.description}
						>Link description</TextArea>
					</div>
				</div>
			</li>
		)
	}

	const setImageFocus = (e) => {
		if(!is_image) return;

		const rect = e.target.getBoundingClientRect();
		const x = e.clientX - rect.left; //x position within the element.
		const y = e.clientY - rect.top;  //y position within the element.

		props.changeMedia('offset', media.index, {
			left:Math.round(100 * x / rect.width),
			top:Math.round(100 * y / rect.height),
		});
	};

	const image_offset = (is_image) ? (media?.offset ?? { left:50, top:50 }) : false;

	const min_caption_length = stage.media_caption_mandatory ? (stage?.media_caption_min ?? 0) : 0;
	const max_caption_length = stage.media_caption_mandatory ? (stage?.media_caption_max ?? 1500) : 0;
	return (
		<li class={`uploading_media_block is_media is_draggable_item awesome is_${media.status}`}>
			{(with_dragger) && (
				<>
					{(is_drag_enabled) ? (
						<DragHandle height={thumb_h} />
					) : (
						<div class="entry_sorter_target" style={{height:`${thumb_h}px`}}>
							<span class="dragging_icon not_enabled knk-loading" />
						</div>
					)}
				</>
			)}

			<div class="media_info">
				<div class="media_preview is_ltr">
					<div>
						<svg class="default_svg" width={thumb_w} height={thumb_h} xmlns="http://www.w3.org/2000/svg">
							{(!file_completed_not_pdf && !is_pdf || is_pdf && !file_completed_pdf) && (
								<defs>
									<filter id={`filter_${index}`}>
										<feGaussianBlur in="SourceGraphic" stdDeviation="1.5" />
										<feColorMatrix type="matrix" values="0.33 0.33 0.33 0 0 0.33 0.33 0.33 0 0 0.33 0.33 0.33 0 0 0 0 0 1 0" />
									</filter>

									<linearGradient id={`gradient_${index}`} x1="0" y1="0" x2="100%" y2="0">
										<stop offset="0" style={{stopColor:'#FFFFFF', stopOpacity:'0.3'}} />
										<stop offset="100%" style={{stopColor:'#FFFFFF', stopOpacity:'0'}} />
									</linearGradient>
								</defs>
							)}

							<image
								href={media_thumbnail ??  `${window.cdn_url}img/video_processing_thumb.jpg`}
								width={thumb_w}
								height={thumb_h}
								preserveAspectRatio="xMidYMid slice"
							/>

							{(!file_completed_not_pdf && !is_pdf || is_pdf && !file_completed_pdf) && (
								<g>
									<rect class="loading_img" width={thumb_w} height={thumb_h} fill={'rgba(255, 255, 255, 0.3)'} />

									{(media.status != 'failed') ? (
										<rect class="uploading_size_bg" x={0} y={thumb_h - 21} width={thumb_w} height={20} />
									) : (
										<rect fill={error_ui_color} x={0} y={thumb_h - 21} width={thumb_w} height={20} />
									)}

									<text
										class="hb_mainFont hb_mainFontWeightBold"
										x={5}
										y={thumb_h - 7}
										fontSize={12}
										fill={uploader_ui_color}
									>{label_status}</text>

									<g class="loading_thumb" clipPath={`inset(0 0 0 ${100 * thumb_progress}%)`}>
										<rect width={thumb_w} height={thumb_h} fill={uploader_ui_color} />

										<image class="loading_img" href={media_thumbnail} width={thumb_w} height={thumb_h} filter={`url(#filter_${index})`} />

										<rect x={0} y={thumb_h - 21} width={thumb_w} height={20} fill="rgba(0, 0, 0, 0.5)" />
									</g>

									<rect fill={`url(#gradient_${index})`} width={thumb_w} height={thumb_h} x={loading_x} y={0} />

									<line stroke={uploader_ui_color} strokeWidth={1} x1={loading_x + 0.5} y1={0} x2={loading_x + 0.5} y2={thumb_h} />

									<text
										class="hb_mainFont hb_mainFontWeightBold"
										x={loading_x + 5}
										y={thumb_h - 7}
										fontSize={12}
										fill={uploader_ui_color}
									>{(media_p < 1) ? `0% - ${i18n("Processing")}` : `${media_p}%`}</text>
								</g>
							)}

							<rect width={thumb_w - 1} height={thumb_h - 1} stroke={uploader_ui_border_color} fill="transparent" strokeWidth={1} x={0.5} y={0.5} />
						</svg>

						{(image_offset !== false) && (
							<>
								<div class="focus_point_container" style={{ aspectRatio:`${thumb_w} / ${thumb_h}` }}>
									<span
										class="focus_point knk-focus-point"
										style={{
											top:`calc(${image_offset?.top ?? 50}% - 0.8em)`,
											left:`calc(${image_offset?.left ?? 50}% - 0.8em)`,
										}}
									/>

									<div
										class="focus_point_area"
										onClick={(e) => setImageFocus(e)}
										onTap={(e) => setImageFocus(e)}
									/>
								</div>

								<p class="focus_point_tip knk-focus-point">{i18n("Click to center")}</p>
							</>
						)}
					</div>

					{(file_completed_pdf && is_pdf || file_completed_not_pdf && !is_pdf || media.status == 'failed') && (
						<div class="media_completed_actions">
							<ActionLink onClick={() => props.deleteFile(media)}>Delete</ActionLink>

							{(media.status == 'completed') && (
								<span class="completed_media">
									<svg class="ext_border" width="18px" height="18px" viewBox="0 0 18 18">
										<circle r="9" cx="9" cy="9" fill="green" />

										<g transform="translate(3.5 5) scale(0.6)">
											<polyline
												class="completed_icon"
												fill="transparent"
												points="18,0 6,12 0,6"
												stroke="white"
												strokeDasharray="25.46 25.46"
												strokeWidth="2"
											/>
										</g>
									</svg>
								</span>
							)}
						</div>
					)}
				</div>

				{((stage?.media_caption_hidden ?? false) === false) && (
					<div class="media_caption">
						<TextArea
							_class={{first:true}}
							label_props={{
								mandatory:stage.media_caption_mandatory,
							}}
							disabled={is_builder/* || media.xxss != undefined*/}
							errors={error_media}
							name="caption"
							change={(k, v) => props.changeMedia('caption', media.index, v)}
							value={media.caption}
							min_length={min_caption_length > 0 ? min_caption_length : null}
							max_length={max_caption_length}
						>Caption</TextArea>
					</div>
				)}
			</div>
		</li>
	)
});

const MediaParticipation = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	useCSS('comp/multigallery.css');

	const is_builder = props?.is_builder ?? false;
	const is_edit = props?.is_edit ?? false;
	const is_edit_builder = props?.is_edit_builder ?? false;
	const is_add_builder = props?.is_add_builder ?? false;
	const is_debug = (is_builder) ? true : false;

	const stage = props.stage.Stage;
	const media_has_step_0 = stage?.media_has_step_0 ?? true;

	const initial_step = props?.initial_step ?? ((media_has_step_0) ? 0 : 1);
	const [ step, setStep ] = useState(initial_step);
	const participation = props?.participation ?? false;

	const image_permitted = stage?.media_image ?? false;
	const video_permitted = stage?.media_video ?? false;
	const youtube_permitted = stage?.media_video_url ?? false;
	const pdf_permitted = stage?.media_pdf ?? false;
	
	const text_permitted = stage?.media_text ?? false;
	const url_permitted = stage?.media_url ?? false;
	const tiktok_permitted = stage?.media_tiktok ?? false;
	const has_cover_enabled = (stage.media_title || stage.media_title_checkbox) ? true : false;
	const uploaded_permitted = (image_permitted || video_permitted || pdf_permitted) ? true : false;

	let type_videoimage = false;
	if(uploaded_permitted) type_videoimage = true;
	if(youtube_permitted) type_videoimage = true;
	if(tiktok_permitted) type_videoimage = true;

	const is_only_text = (text_permitted && !(url_permitted || type_videoimage)) ? true : false;
	const is_only_url = (url_permitted && !(text_permitted || type_videoimage)) ? true : false;

	

	const remove_name = {};
	if(participation == false) remove_name.name = "";

	const [ user, setUser ] = useState({ ...window.login_info, ...remove_name });

	let debug_medias = [];
	if(is_debug) {
		for(let index = 0; index < 5; index++) {
			debug_medias.push({
				index:`file_${index + 10}`,
				text:"Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec hendrerit elit ac commodo bibendum. Donec a magna sapien. Aliquam efficitur consectetur nunc, in feugiat sapien luctus vel. Fusce pretium, nisi at consequat suscipit, neque sapien aliquam tortor, malesuada pellentesque lacus quam sit amet arcu. Fusce dui purus, congue at pharetra in, pellentesque a ipsum. Maecenas cursus bibendum leo, eget volutpat justo malesuada vel. In fermentum sapien augue, sed accumsan mauris convallis vel. Integer ultrices commodo nulla, quis pharetra neque condimentum nec. Integer lorem leo, convallis sit amet sollicitudin ut, ultrices sed justo. Cras quis gravida neque, a venenatis elit. Aenean tincidunt tellus eu ipsum commodo, ut volutpat arcu ultricies. Praesent ac pellentesque neque, vel dictum massa. Donec a justo vel orci rutrum feugiat eget nec ipsum. Curabitur ut lobortis nisi, non pellentesque lectus.",
				type:'text',
			});
		}

		for(let index = 0; index < 5; index++) {
			debug_medias.push({
				index:`file_${index + 5}`,
				url:'https://www.easypromosapp.com/',
				title:'Easypromos',
				description:"",
				type:'url',
			});
		}

		for(let index = 0; index < 5; index++) {
			debug_medias.push({
				caption: "🔥 ¡WOW! Ya puedes crear apps, vídeos y diseños SOLO con IA | Noticias Tecnología y Redes Sociales",
				height:113,
				index:`file_${index + 15}`,
				status:"completed",
				theight:360,
				thumbnail:"https://i.ytimg.com/vi/j-PR6ZYs3Pk/hqdefault.jpg",
				twidth:480,
				type:"youtube",
				url:"https://www.youtube.com/embed/j-PR6ZYs3Pk",
				width:200,
				youtube_id:"j-PR6ZYs3Pk"
			});
		}

		for(let index = 0; index < 5; index++) {
			debug_medias.push({
				caption: "¿Cómo funciona el ALGORITMO de TIKTOK? | Easypromos TV",
				height:113,
				index:`file_${index + 15}`,
				status:"completed",
				theight:360,
				thumbnail:"https://i.ytimg.com/vi/3FRrbe2b5ok/hqdefault.jpg",
				twidth:480,
				type:"tiktok",
				url:"https://www.youtube.com/embed/3FRrbe2b5ok",
				width:200,
				youtube_id:"j-PR6ZYs3Pk"
			});
		}

		debug_medias.push({
			index:'file_0',
			status:'completed',
			progress:99,
			realname:'medinet-habu.jpg',
			type:'image',
			size:272000,
			width:360,
			height:180,
			thumbnail:`${window.cdn_url}img/example_photos/medinet_habu.jpg`,
			caption:i18n("The afternoon light at Medinet Habu temple (Luxor West Bank)"),
			offset:{
				left:44,
				top:70,
			},
		}, {
			index:'file_1',
			status:'in_progress',
			progress:62,
			realname:'barcelona.jpg',
			type:'image',
			size:188000,
			width:360,
			height:240,
			thumbnail:`${window.cdn_url}img/example_photos/barcelona.jpg`,
			caption:i18n("Barcelona skyline at night viewed from the Búnquers del Carmel"),
			offset:{
				left:50,
				top:30,
			},
		}, {
			index:'file_2',
			status:'to_server',
			progress:78,
			realname:'vernazza.jpg',
			type:'image',
			size:323000,
			width:360,
			height:215,
			thumbnail:`${window.cdn_url}img/example_photos/vernazza.jpg`,
			caption:i18n("Vernazza (Cinque Terre, Italy) at blue hour"),
			offset:{
				left:50,
				top:30,
			},
		}, {
			index:'file_3',
			status:'in_progress',
			progress:21,
			realname:'firenze.jpg',
			type:'image',
			size:381000,
			width:360,
			height:333,
			thumbnail:`${window.cdn_url}img/example_photos/firenze.jpg`,
			caption:i18n("The Duomo and the Campanile of the amazing Firenze (Italy)"),
			offset:{
				left:33,
				top:42,
			},
		}, {
			index:'file_4',
			status:'in_progress',
			progress:42,
			realname:'valencia.jpg',
			type:'image',
			size:428000,
			width:360,
			height:734,
			thumbnail:`${window.cdn_url}img/example_photos/valencia.jpg`,
			caption:i18n("Detail of the Ciudad de las Ciencias y las Artes (Valencia)"),
			offset:{
				left:48,
				top:65,
			},
		});
	}

	let init_currents = 0;
	let default_medias = debug_medias;
	let default_texts = [];
	if(is_only_text) {
		for(let index = 0; index < parseInt(stage.media_min_elements); index++) {
			default_texts.push({
				index:`file_${index}`,
				text:'',
				type:'text',
			});
			if(!is_debug) init_currents++;
		}
		default_medias = default_texts;
	}

	const [ state, dispatch ] = useReducer(reducer, {
		title:"",
		description:"",
		youtube:"",
		url:"",
		tags:props?.participation?.quiz_entry?.tags ?? [],
		color:(isNotEmpty(stage, 'media_colors')) ? (stage.media_colors).split("\n")[0] : undefined,
		errorFile:false,
		showFormErrors:false,
	});
	
	const sliceMediasToMax = () => {

		const shuffleArray = (array) => {
			const arr = [...array]; // fem còpia per no mutar l'original
			for (let i = arr.length - 1; i > 0; i--) {
				const j = Math.floor(Math.random() * (i + 1));
				[arr[i], arr[j]] = [arr[j], arr[i]]; // intercanvia valors
			}
			return arr;
		}

		const allowed_types = [];

		if (image_permitted) {
			allowed_types.push('picture');
			allowed_types.push('image');
		}
		if (video_permitted) allowed_types.push('video');
		if (youtube_permitted){
			allowed_types.push('link');
			allowed_types.push('youtube');
		}
		if (text_permitted) allowed_types.push('text');
		if (pdf_permitted) allowed_types.push('pdf');
		if (url_permitted) allowed_types.push('url');
		if (tiktok_permitted) allowed_types.push('tiktok');

		const filtered_medias = shuffleArray(default_medias).filter(media => allowed_types.includes(media.type));

		const grouped = {};
			for (const media of filtered_medias) {
			if (!grouped[media.type]) grouped[media.type] = [];
			grouped[media.type].push(media);
		}

		for (const type in grouped) {
			grouped[type] = shuffleArray(grouped[type]);
		}

		//Splice
		const media_max = stage.media_max_elements !== undefined ? parseInt(stage.media_max_elements) : 5;
		const result = [];

		while (result.length < media_max) {
			let addedSomething = false;
			for (const type of Object.keys(grouped)) {
				if (grouped[type].length && result.length < media_max) {
				result.push(grouped[type].shift());
				addedSomething = true;
				}
			}
			if (!addedSomething) break; // no queden més opcions
		}
		return result;
		
	};

	const sliceToMax = (medias) => {
		return medias.slice(0, ((stage.media_max_elements != undefined) ? parseInt(stage.media_max_elements) : 5));
	};

	const [ medias, setMedias ] = useState(sliceMediasToMax());
	const [ errors, setErrors ] = useState({});
	const [ extra_data, setExtraData ] = useState(props?.participation?.quiz_entry?.form ?? false);
	const [ is_form_valid, setIsFormValid ] = useState(true);

	const count_ref = useRef(init_currents);
	const numfiles_ref = useRef(init_currents);
	const completed_ref = useRef(init_currents);
	const dropzone_ref = useRef(null);

	useEffect(() => {
		const params = new URLSearchParams(new URL(window.location.href).search);
		const social = params.get('social');
		if(['tiktok'].includes(social)) {
			setStep(1); //tornem de logged i tenim dades al localstorage per recuperar
		}
	}, []);

	useEffect(() => {
		setMedias(sliceMediasToMax());
	}, [ stage.media_max_elements ]);

	useEffect(() => {
		if(!is_debug) return;
		debug_medias = [...debug_medias].filter((o) => {
			if(text_permitted && o.type == 'text') return true;
			if(url_permitted && o.type == 'url') return true;
			if(uploaded_permitted && o.type == 'image') return true;
			return false;
		});

		for(let i = debug_medias.length - 1; i > 0; i--) {
			let j = Math.floor(Math.random() * (i + 1));
			let temp = debug_medias[i];
			debug_medias[i] = debug_medias[j];
			debug_medias[j] = temp;
		}
		setMedias(sliceToMax(debug_medias));
	}, [ url_permitted, text_permitted, uploaded_permitted ]);

	useEffect(() => {
		medias.map((media) => {
			if(media.type == 'pdf' && media.cover_status == 'load' && isNotEmpty(media, 'thumbnail')) {
				upload_thumbnail_pdf(media,(response)=>{
					setMedias((m) => {
						for(let i in m) {
							if(m[i].index != media.index) continue;
							m[i].cover_status = (response.status) ? 'completed' : 'error';
							if(!response.status)m[i].status = 'error';
							if(response.status)m[i].cover_name = response.filename;
							return [ ...m ];
						}

						return m;
					});
				});
			}
		});
	}, [ medias ]);

	const upload_thumbnail_pdf = (media,cb) => {
		setMedias((m) => {
			for(var i in m) {
				if(m[i].index != media.index) continue;

				const media_name = isNotEmpty(media,'name') ? media.name : media.realname;
				const media_upload = {
					base64:media.thumbnail,
					name:media_name.toLowerCase().replace('.pdf','.png'),
				};

				$.post(`/stages/upload/${stage.id}/uploadSize_${getFileSize(base64ToBytes(media_upload.base64), 0).replace(" ", "")}`,media_upload, (response) => {
					if(cb != undefined) cb(response)
				}, 'JSON');

				m[i].cover_status = 'to_server';
				return [ ...m ];
			}
			return m;
		});
	}

	const change = (k, v) => {
		if(errors[k] != undefined) delete errors[k];
		dispatch({ type:'CHANGE', k, v });
	};

	const dynamicForm = (data, is_valid) => {
		setExtraData(data);
		setIsFormValid(is_valid);
	};

	const changeMedia = (k, media_index, v) => {
		setMedias((m) => {
			for(let i in m) {
				if(m[i].index != media_index) continue;
				if(errors[media_index] != undefined) delete errors[media_index];
				m[i][k] = v;
				return [...m];
			}
			return m;
		});

	};

	const uploadprogress = (file) => {
		setMedias((m) => {
			for(var i in m) {
				if(m[i].index != file.index) continue;

				const progress = Math.round(file.upload.progress, 2);
				m[i].progress = progress;
				m[i].status = (progress < 100) ? 'in_progress' : 'to_server';

				return [...m];
			}

			return m;
		});
	};

	const completedFile = (file) => {
		let xhr_values = file.xhr != undefined ? file.xhr : false;
		if(xhr_values) {
			let result = JSON.parse(xhr_values.responseText);

			setMedias((m) => {
				for(var i in m) {
					if(m[i].index != file.index) continue;
					if(result.status) {
						m[i].name = result.filename;
						m[i].status = 'completed';
						completed_ref.current++;
					} else {
						m[i].status = 'failed';
					}

					return [...m];
				}

				return m;
			});
		}

		dropzone_ref.current.processQueue();
	};

	const pdfImage = (pdf, page, file) => {
		var viewport = page.getViewport(2.0);
		var canvas = document.createElement("canvas")
		canvas.height = viewport.height;
		canvas.width = viewport.width;

		page.render({
			canvasContext: canvas.getContext('2d'),
			viewport: viewport
		}).promise.then(() => {
			var image = canvas.toDataURL();
			setMedias((m) => {
				for(var i in m) {
					if(m[i].index != file.index) continue;
					m[i].height = canvas.height;
					m[i].width = canvas.width;
					m[i].thumbnail = image;
					//Video thumbnail loaded
					return [...m];
				}
				return m;
			});
		});;
	}

	const videoImage = (reader, file) => {
		const blob = new Blob([reader.result], {type: file.type});
		const url = URL.createObjectURL(blob);
		let video = document.createElement('video');

		var timeupdate = () => {
			if(snapImage()) {
				video.removeEventListener('timeupdate', timeupdate);
				video.pause();
			}
		};

		video.addEventListener('loadeddata', () => {
			if(snapImage()) video.removeEventListener('timeupdate', timeupdate);
		});

		var snapImage = () => {
			var canvas = document.createElement('canvas');
			canvas.width = video.videoWidth;
			canvas.height = video.videoHeight;
			canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
			const image = canvas.toDataURL();
			var success = (image.length > 100000) ? true : false;
			//if(success) {
				setMedias((m) => {
					for(var i in m) {
						if(m[i].index != file.index) continue;
						m[i].height = success ? canvas.height : 550;
						m[i].width = success ? canvas.width : 800;
						m[i].thumbnail = success ? image : `${window.cdn_url}img/video_processing_thumb.jpg`;
						//Video thumbnail loaded
						return [...m];
					}
					return m;
				});

				URL.revokeObjectURL(url);
			//}

			return success;
		};

		video.addEventListener('timeupdate', timeupdate);
		video.preload = 'metadata';
		video.src = url;
		// Load video in Safari / IE11
		video.muted = true;
		video.playsInline = true;
		video.play();
	};

	const getFileType = (file_data) => {
		if(file_data.indexOf('image/') !== -1) return 'image';
		if(file_data.indexOf('video/') !== -1) return 'video';
		if(file_data.indexOf('audio/') !== -1) return 'audio';
		if(file_data.indexOf('application/pdf') !== -1) return 'pdf';
		return false;
	};

	const fileAdded = (file) => {
		var reader = new FileReader();
		file.index = `file_${count_ref.current}`;
		let type_media = getFileType(file.type);

		reader.onload = async (e) => {
			file.skip = (file.status != 'error' && numfiles_ref.current < stage.media_max_elements) ? false : true;
			if(type_media == 'video' && !file.skip) {
				// Video thumbnail loading
				videoImage(reader, file);
			}

			if(!file.skip) {
				let new_media = {
					index:file.index,
					name:"",
					progress:0,
					realname:file.name,
					size:file.size,
					status:(!file.skip) ? 'load' : 'skip',
					cover_status:(!file.skip && type_media == 'pdf') ? 'load' : 'skip',
					type:type_media,
				};
				if (type_media == 'image') {
					if(new_media.height == undefined) new_media.height = file?.height || default_thumb_w;
					if(new_media.width == undefined) new_media.width = file?.width || default_thumb_w;
					if(new_media.thumbnail == undefined) new_media.thumbnail = e.target.result;
				}
				setMedias((m) => [...m, new_media]);

				if(type_media == 'pdf') {
					var typeArray = new Uint8Array(e.target.result);
					let pdf = await PDFJS.getDocument(typeArray);
					const page = await pdf.getPage(1)
					pdfImage(pdf,page,file);
				}
				numfiles_ref.current++;
			} else if(file.status == 'error') {
				dispatch({ type:'CHANGE', k:'errorFile', v:file });
				dropzone_ref.current.removeFile(file);
			}
		}

		count_ref.current++;

		if(type_media == 'video' || type_media == 'pdf') {
			reader.readAsArrayBuffer(file);
		} else {
			reader.readAsDataURL(file);
		}
	};

	const deleteFile = (file_index) => {
		setMedias((medias) => {
			return medias.filter((m) => m.index != file_index.index);
		});
		numfiles_ref.current--;
	};

	const closeError = () => {
		dispatch({ type:'CHANGE', k:'errorFile', v:false });
	};

	const validate = (force_check) => {
		if(force_check == undefined) force_check = [];
		Object.keys(errors).map((keyName) => { delete errors[keyName]; }); //Delete Errors

		if(is_builder) return;

		switch(step) {
			case 0:
				const hide_nomination_name = stage?.hide_media_name_label ?? false; // Camp antic
				const enable_name = stage?.media_has_name_label ?? !hide_nomination_name; //Posar '?? true' quan considerem que el camp vell no s'utilitza/afecta mes
				if(enable_name && stage.media_name_label && !user.name) errors['name'] = i18n("This field is mandatory");
				if(stage.media_has_country && !user.country) errors['country'] = i18n("This field is mandatory");
				//if(stage.media_has_avatar && !user.avatar) errors['hashtag_avatar'] = i18n("This field is mandatory");

				if(stage.media_has_short_description && !isNotEmpty(user.extra,'short_description',[''])) {
					let errors_extra = errors?.extra ?? {};
					errors_extra['short_description'] = i18n("This field is mandatory");
					errors['extra'] = errors_extra;
				}

				/*if(stage.media_has_links && !isNotEmpty(user.extra,'external_link',[''])) {
					let errors_extra = errors?.extra ?? {};
					errors_extra['external_link'] = i18n("This field is mandatory");
					errors['extra'] = errors_extra;
				}*/
				break;
			case 1:
				for(var i in medias) {
					
					let is_media_text = medias[i].type == 'text';
					let is_media_url = medias[i].type == 'url';

					if(is_media_url && (!validateURL(medias[i].url) || medias[i].title.length <= 0)) {
						errors[medias[i].index] = i18n("This field is mandatory");
					} else if(is_media_text && medias[i].text.length <= 0) {
						errors[medias[i].index] = i18n("This field is mandatory");
					} else if(!is_media_text && !is_media_url && stage.media_caption_mandatory && !medias[i].caption && medias[i].status !== 'skip' && (stage?.media_caption_hidden ?? false) === false) {
						errors[medias[i].index] = i18n("This field is mandatory");
					}
				}

				if(state.youtube != '' || force_check['youtube'] != undefined) {
					let url = state.youtube;
					url.match(/(http:|https:|)\/\/(player.|www.)?(youtu(be\.com|\.be|be\.googleapis\.com))\/(video\/|videos\/|embed\/|watch\?v=|v\/)?([A-Za-z0-9._%-]*)(\&\S+)?/);
					let ko_regexp = RegExp.$3.indexOf('youtu') == -1;
					if(ko_regexp) {
						errors['youtube'] = i18n("Invalid YouTube URL");
					}
				}

				if(state.url != '' || force_check['url'] != undefined) {
					let ko_regexp = !validateURL(state.url);
					if(ko_regexp) {
						errors['url'] = i18n("Invalid URL");
					}
				}
				break;

			case 2:
				if(stage.media_title && !state.title) errors['title'] = i18n("The title is mandatory");

				if(stage.media_title && stage.media_description && stage.media_description_mandatory != undefined && stage.media_description_mandatory && !state.description) {
					errors['description'] = i18n("The description is mandatory");
				}
				break;
		}

		//if(Object.keys(errors).length > 0)
		setErrors({ ...errors });
	};

	const addYoutube = (cb, youtubeurl) => {
		if (youtubeurl != undefined) state.youtube = youtubeurl;
		validate({ youtube:true });
		if(errors.youtube == undefined) {
			let url = state.youtube;
			url.match(/(http:|https:|)\/\/(player.|www.)?(youtu(be\.com|\.be|be\.googleapis\.com))\/(video\/|videos\/|embed\/|shorts\/|watch\?v=|v\/)?([A-Za-z0-9._%-]*)(\&\S+)?/);
			var video_id = RegExp.$6;
			var platform = 'youtube';
			var video_url = `https://www.youtube.com/embed/${video_id}`;
			var url_info = '&showinfo=0&controls=0&rel=0';

			var r = url.match(/t=(.*?)s\&?/);
			var e = url.match(/e=(.*)s/);

			if(r && r[1] != undefined) url_info+='&start='+r[1];	// start time parameter
			if(e && e[1] != undefined) url_info+='&end='+e[1];		// end time parameter
			$.get('https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v='+video_id, (res) => {
				let new_media = {
					index:`file_${count_ref.current}`,
					url:video_url,
					youtube_id:video_id,
					caption:res.title,
					thumbnail:res.thumbnail_url,	//'https://i.ytimg.com/vi/'+video_id+'/maxresdefault.jpg'
					status:'completed',
					width:res.width,
					height:res.height,
					twidth:res.thumbnail_width,		//res.width
					theight:res.thumbnail_height,
					type:'youtube',
				};

				numfiles_ref.current++;
				completed_ref.current++;
				count_ref.current++;
				setMedias((m) => [...m, new_media]);
				change('youtube', "");
				validate();
			}, 'JSON').fail((xhr, ajaxOptions, thrownError) => {
				let _errors = {'youtube':`Youtube Error: ${xhr?.responseText ?? xhr.responseText}`};
				setErrors({ ..._errors });
			});
		}
	};

	const addText = (cb) => {
		let new_media = {
			index:`file_${count_ref.current}`,
			text:'',
			type:'text',
		};

		numfiles_ref.current++;
		completed_ref.current++;
		count_ref.current++;
		setMedias((m) => [...m, new_media]);
		if(cb != undefined) cb();
	};

	const domainFromUrl = (url) => {
		var result;
		var match;
		if(match = url.match(/^(?:https?:\/\/)?(?:[^@\n]+@)?(?:www\.)?([^:\/\n\?\=]+)/im)) {
			result = match[1]
			if (match = result.match(/^[^\.]+\.(.+\..+)$/)) {
				result = match[1]
			}
		}
		return result;
	};

	const addLink = (cb) => {
		validate({ url:true });
		if(errors.url == undefined) {
			let new_media = {
				index:`file_${count_ref.current}`,
				url:state.url,
				title:domainFromUrl(state.url),
				description:'',
				type:'url',
			};

			numfiles_ref.current++;
			completed_ref.current++;
			count_ref.current++;
			setMedias((m) => [...m, new_media]);
			change('url', "");	
			if(cb != undefined) cb();
		}
	};

	const simulateUploading = (media, size, cb) => {
		let timeToComplete = size / 500;
		let increment = 100 / (timeToComplete / 100); 
		let progress = 0;
		const interval = setInterval(() => {
			progress += increment;
			setMedias((m) => {
				for(var i in m) {
					if(m[i].index != media.index) continue;
	
					const _progress = Math.round(progress, 2);
					m[i].progress = _progress;
					m[i].status = (_progress < 100) ? 'in_progress' : 'to_server';
					return [...m];
				}
				return m;
			});

			if (progress >= 100) {
				clearInterval(interval);
				if(cb != undefined)cb();
			}
		}, 100); // Update every 100ms
	}

	const addFromImgUrl = (posts, subtype, cb) => {
		posts.map((post, index) => {
			const img = new Image();
			img.src = post.picture;
			img.onload = () => { 
				const _width = this?.width ?? 400;
				const _height = this?.height ?? 400;
				var xhr = new XMLHttpRequest();
				xhr.open("GET", post.picture, true);
				xhr.responseType = "arraybuffer";
				xhr.onreadystatechange = function() {
					if(this.readyState == this.DONE) {
						const _size = this.response.byteLength;
						let new_media = {
							index:`file_${count_ref.current}`,
							url:post.link,
							instagram_id:post.id,
							caption:post.name,
							thumbnail:post.picture,	//'https://i.ytimg.com/vi/'+video_id+'/maxresdefault.jpg'
							status:'in_progress',
							progress:10,
							size:_size,
							width:_width,
							height:_height,
							type:'image',
							xxss:subtype
						};
						numfiles_ref.current++;
						count_ref.current++;
						
						setMedias((m) => [...m, new_media]);

						let media_upload = {};
						media_upload.image_url = post.picture;
						media_upload.name = (`file_${count_ref.current}.jpg`);
						simulateUploading(new_media,_size, ()=>{
							$.post(`/stages/upload/${stage.id}/uploadSize_${_size}_${subtype}`,media_upload,(response)=>{
								setMedias((m) => {
									for(var i in m) {
										if(m[i].index != new_media.index) continue;
										m[i].status = response.status ? 'completed' : 'error';
										if(response.status)m[i].name = response.filename;
										completed_ref.current++;
										return [...m];
									}
									return m;
								});
							},'JSON').fail((xhr, ajaxOptions, thrownError) => {
								setMedias((m) => {
									for(var i in m) {
										if(m[i].index != new_media.index) continue;
										m[i].status = 'failed';
										return [...m];
									}
									return m;
								});
							});;
						});
					}
				};
				xhr.send(null);
			}
		});
	};

	const submitMedias = (cb) => {
		if(!is_form_valid) {
			dispatch({ type:'CHANGE', k:'showFormErrors', v:true });
			cb();
			return;
		}
		validate();

		if(Object.keys(errors).length > 0) {
			if(cb != undefined) cb();
			return;
		}

		let send_object = { ...user,quiz_entry:{} };
		if(tags.length > 0) send_object['quiz_entry'].tags = state.tags;
		if(stage.media_title) send_object['quiz_entry'].title = state.title;
		if(stage.media_description) send_object['quiz_entry'].description = state.description;
		if(stage.media_colors) send_object['quiz_entry'].color = state.color;
		if(stage.entry_premoderation && !is_edit && !is_edit_builder && !is_add_builder) send_object['public_entry'] = 0;

		let cloned_medias = JSON.parse(JSON.stringify(medias));//necessary to not lose thumbs in SingleGallery when submit

		for(let i in cloned_medias) {
			if(cloned_medias[i].status == 'skip') {
				delete cloned_medias[i];
				continue;
			}

			if(cloned_medias[i].twidth !== undefined) delete cloned_medias[i].twidth;
			if(cloned_medias[i].theight !== undefined) delete cloned_medias[i].theight;
			if(cloned_medias[i].type == 'video' && !cloned_medias[i].status_video) cloned_medias[i].status_video = 'pending';
			if(cloned_medias[i].type != 'youtube') {
				delete cloned_medias[i].realname;
				delete cloned_medias[i].progress;
				delete cloned_medias[i].size;
			}
			delete cloned_medias[i].index;
			delete cloned_medias[i].thumbnail;
			delete cloned_medias[i].status;
			if((is_edit || is_edit_builder || is_add_builder) && ['pdf','image','video'].indexOf(cloned_medias[i].type) > -1) {
				cloned_medias[i].full_path = imgPath(EP.BUCKET, props.promotion.id, 'media');
			}

			if(cloned_medias[i].cover_status !== undefined) delete cloned_medias[i].cover_status;
		}

		send_object.quiz_entry.media = cloned_medias;

		if(extra_data != false) send_object['quiz_entry'].form = extra_data;
		if(props.submitMedias != undefined) {
			props.submitMedias(send_object, (res) => {
				if(res.status) {
					if(res.next_participation != undefined) {
						var next_participation = res.next_participation;
						if(next_participation !== false && next_participation !== true) {
							var time_to_next_participation = EPDateDuration(EPDateDiff(next_participation));
							res.next_participation = EPDateHumanize(time_to_next_participation);
						} else {
							res.next_participation = next_participation;
						}
					}
					if(res.status && res.url) {
						window.location.href = res.url;
						return;
					}
				}
				if(cb != undefined) cb();
			});
			return;
		}

		if(props.updateParticipation != undefined) {
			props.updateParticipation(participation.id, send_object);
		}
		if(cb != undefined) cb();
	};

	const goToStep = (_step, cb) => {
		if(is_builder) return;
		validate();
		if(!is_form_valid) {
			dispatch({ type:'CHANGE', k:'showFormErrors', v:true });
			cb();
			return;
		}

		setTimeout(() => {
			adaptHeightOnWidget();
		}, 300);

		if(Object.keys(errors).length == 0) setStep(_step);
		if(cb != undefined) cb();
	};

	const onSortEnd = ({ oldIndex, newIndex }) => {
		const ordered_medias = arrayMove(medias, oldIndex, newIndex);
		setMedias(ordered_medias);
	};

	const accepted_types = [];
	if(stage.media_image) accepted_types.push('Image');
	if(stage.media_video) accepted_types.push('Video');
	if(stage.media_pdf) accepted_types.push('PDF');
	//if(stage.media_audio) accepted_types.push('Audio');

	const has_errors = (Object.keys(errors).length > 0) ? true : false;
	const disabled_add_media = (numfiles_ref.current >= stage.media_max_elements) ? true : false;
	const error_num_files = props.intl.formatMessage({ id:"You have already uploaded the {num_files} files allowed" }, { num_files:numfiles_ref.current });
	const label_title = (stage.media_title != undefined && stage.media_title != "") ? stage.media_title : false;
	
	const colors_arr = (stage.media_colors != undefined && stage.media_colors != "") ? (stage.media_colors).split("\n") : [];

	const label_description = (stage.media_description != undefined && stage.media_description != "") ? stage.media_description : i18n("Description of the album");
	let submit_label = (stage.main_button != undefined && stage.main_button != "" && stage.main_button != " ") ? stage.main_button : i18n("***Submit");
	if(is_edit || is_edit_builder || is_add_builder) submit_label = i18n("Save");

	const label_to_step1 = (is_edit || is_edit_builder || is_add_builder) ? i18n("Edit") : i18n("Go back");

	const has_pdf = medias.filter((m) => m.type == 'pdf').length > 0;
	const all_pdf_processed = medias.filter((m) => m.type == 'pdf' && m.cover_status != 'completed').length == 0;
	//sizes Text
	const min_text_size = parseInt(stage?.media_text_min ?? 0);
	const min_caption_size = parseInt(stage?.media_caption_min ?? 0);
	let all_texts_processed = medias.filter((m) =>  m.type == 'text' && m.text.length <= min_text_size).length == 0;
	let all_urls_processed = medias.filter((m) =>  m.type == 'url' && (!validateURL(m.url) || m.title.length <= 0)).length == 0;
	let all_captions_ok = medias.filter((m) =>  m.type != 'text' && m.type != 'url' && (m?.caption ?? '').length < min_caption_size).length == 0;
	
	let all_medias_processed = medias.filter((m) =>  m.type != 'pdf' && m.type != 'text' && m.type != 'url' && m.status != 'completed' && m.status != 'failed').length == 0;
	if(has_pdf) all_medias_processed = all_pdf_processed;
	const all_medias_ended = medias.filter((m) => (m.status != 'completed' && m.status != 'failed')).length == 0;

	const promotion_media_tags = localizeFields(props.promotion?.available_media_tags ?? [],props.locale);
	const tags = promotion_media_tags.filter((item) => (stage?.media_tags ?? []).includes(item.id));
	const min_tags = parseInt(stage?.media_min_tags ?? 0);
	const max_tags = parseInt(stage?.media_max_tags ?? tags.length);
	const has_min_tags = (min_tags <= state.tags.length) ? true : false;
	const has_max_tags = (max_tags >= state.tags.length) ? true : false;
	const all_tags_selected = (step == 2 && promotion_media_tags.length > 0 && tags.length > 0) ? has_min_tags && has_max_tags : true;
	const submit_enabled = is_builder || is_edit || is_edit_builder || is_add_builder || (numfiles_ref.current >= stage.media_min_elements && completed_ref.current >= numfiles_ref.current && !has_errors) && all_tags_selected;

	const default_description_text = `<h1>${i18n("Submit your entry")}</h1><p>${i18n("Complete all the fields, and remember that you can enter emojis to make your entry stand out and shine.")}</p>`;

	const description_field = (step < 1) ? 'media_description_step0' : 'main_description';
	const page_introduction = (isNotEmpty(stage, description_field)) ? stage[description_field] : default_description_text;

	let main_image = false;
	let main_video = false;
	if(stage.main_image != "") {
		main_image = stage.full_main_image;
	} else if(stage.main_video != "") {
		main_video = stage.main_video;
	}

	let uploaded_medias = [];
	let has_title = (isNotEmpty(stage, 'media_title')) ? true : false;
	let has_description = (is_edit_builder || is_add_builder) ? stage.media_description_checkbox : (isNotEmpty(stage, 'media_description')) ? true : false;

	const toggleTag = (tag_id) => {
		let _user_tags = [ ...state.tags ];
		const index = _user_tags.indexOf(tag_id);

		const min_selected = parseInt(stage?.media_min_tags ?? 1);
		const max_selected = parseInt(stage?.media_max_tags ?? 1);
		if(max_selected == 1 && min_selected == 1) {
			//toggle rapid
			if(_user_tags.length > 0) _user_tags.splice(0, 1);
			_user_tags.push(tag_id);
		} else if(index === -1) {
			if(_user_tags.length < max_selected) _user_tags.push(tag_id);
		} else {
			_user_tags.splice(index, 1);
		}

		change('tags', _user_tags);
	};

	useEffect(() => {
		if(!is_edit && !is_edit_builder) return;

		let medias = [];
		const qz = participation.quiz_entry;
		(qz?.media ?? []).map((media, j) => {
			let _is_pdf = media.type == 'pdf';
			let _is_video = media.type == 'video';
			let _is_youtube = media.type == 'youtube';
			media.index = `file_${j}`;
			media.status = 'completed';

			if(_is_pdf) media.cover_status = 'completed';
			if(_is_video) media.status_video = 'completed';
			if(_is_youtube){
				media.thumbnail = 'https://i.ytimg.com/vi/'+media.youtube_id+'/hqdefault.jpg'
			} else if(_is_video) {
				if(isNotEmpty(media, 'full_path') && isNotEmpty(media, 'video_thumb')) {
					media.thumbnail = `${media.full_path}${media.video_thumb}`;
				} else {
					media.thumbnail = `${window.cdn_url}img/video_processing_thumb.jpg`;
				}
			} else if(_is_pdf) {
				media.thumbnail = `${media.full_path}normal/${media.cover_name}`;
			} else {
				media.thumbnail = `${media.full_path}normal/${media.name}`;
			}
			medias.push(media);
			numfiles_ref.current++;
			completed_ref.current++;
			count_ref.current++;
		});

		setMedias(medias);

		change('color', qz?.color ?? '');
		if(has_title) change('title', qz?.title ?? '');
		if(has_description) change('description', qz?.description ?? '');
	}, [ participation ]);

	if(step == 2) {
		if(has_title) {
			const _title = ((is_edit_builder || is_add_builder) && label_title === true) ? i18n("Title") : label_title 
			//const _description = ''

			uploaded_medias.push({
				description:(has_description) ? ((state.description != undefined && state.description != "") ? state.description : `[ ${label_description} ]`) : '',
				title:(state.title != undefined && state.title != "") ? state.title : `[ ${_title} ]`,
				type:'cover',
				width:100,
				height:50,
			});
		}

		medias.map((media) => {
			uploaded_medias.push({
				name:media?.name ?? "",
				type:media.type,
				height:media?.height ?? 0,
				width:media?.width ?? 0,
				caption:media?.caption ?? "",
				text:media?.text ?? "",
				url:media?.url ?? "",
				description:media?.description ?? "",
				title:media?.title ?? "",
				base64_code:media?.thumbnail ?? "",
				offset:media?.offset ?? null,
			});
		});
	}

	const formFileUpload = (base64, field, cb) => {
		$.post(`/promotions/upload_form/${props.promotion.id}`, { file:base64 }, (response) => {
			if(cb != undefined) cb(response);
		}, 'JSON');
	};

	const media_origins_arr = [];
	//if(accepted_types.length > 0) media_origins_arr.push({ type:'upload', icon:'knk-upload' });
	if(image_permitted) media_origins_arr.push({ type:'picture', upload_type:'upload', icon:'knk-picture' });
	if(video_permitted) media_origins_arr.push({ type:'video', upload_type:'upload', icon:'knk-video-contest' });
	if(youtube_permitted) media_origins_arr.push({ type:'link', upload_type:'link', icon:'ep-youtube' });
	if(text_permitted) media_origins_arr.push({ type:'text', upload_type:'text', icon:'knk-text' });
	if(pdf_permitted) media_origins_arr.push({ type:'pdf', upload_type:'upload', icon:'knk-page' });
	if(url_permitted) media_origins_arr.push({ type:'url', upload_type:'url', icon:'knk-links' });
	if(tiktok_permitted) media_origins_arr.push({ type:'tiktok', upload_type:'tiktok', icon:'ep-tik-tok' });
	
	const initDropzone = (dropzone) => {
		dropzone_ref.current = dropzone;
	};

	let user_info = { name:`[ ${i18n("Your name")} ]` };
	if(window.login_info) {
		user_info = window.login_info;
		if(step == 2 && user.name == '' && isNotEmpty(user_info, 'name',[''])){//Control per tal que en la SingleGallery aparegui un nom, és nomes efecte visual ja que sino es demana nom a Step0 a la galeria ve buit.
			user.name = user_info.name;
		}
	} else if((is_edit || is_edit_builder || is_add_builder) && participation) {
		user_info = participation;
	} else if(window.owner_main_vars != undefined && (window.owner_main_vars?.agent?.Agent?.name ?? false)) {
		user_info.name = `${window.owner_main_vars.agent.Agent.name} ${window.owner_main_vars.agent.Agent.lastname}`;
		user_info.avatar = window.owner_main_vars.agent.Agent?.full_picture ?? null;
		user_info.country = window.owner_main_vars.agent.Agent?.country ?? null;

		if(media_has_step_0 && (stage?.media_has_short_description ?? true)) {
			user_info.extra = {};
			user_info.extra.short_description = "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Etiam efficitur auctor fringilla. Donec eget odio cursus dui porta sagittis in non elit. Nam mollis tortor vel laoreet vulputate. In sed vehicula eros, ut sollicitudin augue.";
		}

		user_info.force_avatar = true;
	}
	const disabled_submit_step_0 = has_errors;
	const disabled_submit_step_2 = (!submit_enabled || !all_medias_processed) ? true : false;
	const disabled_submit_step_1 = (disabled_submit_step_2 || !all_texts_processed || !all_urls_processed || !all_captions_ok) ? true : false;

	let cont_classes = [];
	if(!is_edit && !is_edit_builder && !is_add_builder) {
		cont_classes.push('gui_main_text_color_on_bg __gui_not_in_top_huge');
	} else if(step == 2 && !is_edit_builder && !is_add_builder) {
		cont_classes.push('gui_not_in_top');
	}

	if(step == 0) {
		cont_classes.push('is_step_0 gui_form_register');
	}

	const display_real_preview = (!is_edit_builder && !is_add_builder) ? true : false;

	const preview_classes = [];
	if(display_real_preview) {
		preview_classes.push('gallery_preview');
		if(!is_edit) preview_classes.push('__gui_not_in_top_big original_color_item');
	} else {
		preview_classes.push('gallery_preview_in_edit');
	}

	const has_cover = (has_cover_enabled || text_permitted) ? true : false;

	let has_medias = false;
	let has_media_to_cover = false;

	medias.map((media) => {
		has_medias = true;
		if(media.type == 'text') has_media_to_cover = true;
	});

	const has_extra_fields = (stage.media_form && stage.media_form.length > 0) ? true : false;

	let step_2_styles = null;
	if((has_cover_enabled || has_media_to_cover || has_extra_fields !== false) && (is_edit || is_edit_builder || is_add_builder)) {
		step_2_styles = {
			display:'grid',
			gridTemplateColumns:'repeat(2, 1fr)',
			gap:'2em',
		};
	}

	const callBackFromSocialNetwork = (data) => {
		const _user = data?.user ?? {};
		setUser(_user);
		const _medias = (data?.medias ?? []).filter((m) => m.status == 'completed');
		setMedias(_medias);
		numfiles_ref.current = _medias.length;
		completed_ref.current = _medias.length;
		count_ref.current = _medias.length;
		setStep(1);
	};

	const display_cover_fields = (has_cover && (label_title || has_description || colors_arr.length > 1)) ? true : false;
	const display_extra_fields = ((is_edit || is_edit_builder || is_add_builder) && has_extra_fields) ? true : false;
	return (
		<Wrapper
			main_image={main_image}
			main_video={main_video}
			is_edit={is_edit}
			is_edit_builder={is_edit_builder}
			is_add_builder={is_add_builder}
			step={step}
			media_has_step_0={media_has_step_0}
			page_introduction={page_introduction}
			disabled_submit_step_0={disabled_submit_step_0}
			disabled_submit_step_1={disabled_submit_step_1}
			disabled_submit_step_2={disabled_submit_step_2}
			goToStep={goToStep}
			submitMedias={submitMedias}
			participation={participation}
			intl={props.intl}
			onCloseLayer={props.onCloseLayer}
			layer_actions={props.layer_actions}
			has_medias={has_medias}
		>	
			{(step == 0) && (
				<div class={cont_classes.join(" ")}>
					<Step0
						stage={{ Stage:stage }}
						errors={errors}
						User={user}
						promotion={props.promotion}
						is_frontend={true}
						changeUser={(k, v) => {
							const { [k]: _, ...newErrors } = errors;
							setErrors(newErrors);
							setUser((u) => {
								return {
									...u,
									[k]:v
								};
							})}
							}
						intl={props.intl}
					>
						{(has_extra_fields) && (
							<DynamicForm
								user={extra_data}
								fileUpload={formFileUpload}
								form={localizeFields((stage?.media_form ?? []), props.locale)}
								onChange={dynamicForm}
								showFormErrors={state.showFormErrors}
							/>
						)}
					</Step0>

					<div class="finalize_form enter flex_button gui_big_button gui_not_in_top_big dis_wo_signin">
						<SaveButton
							color="variable"
							disabled={disabled_submit_step_0}
							_class={{ other:'original_color_item' }}
							onClick={(e, cb) => goToStep(1, cb)}
						>Continue</SaveButton>
					</div>
				</div>
			)}

			{(step == 1) && (
				<div class={cont_classes.join(" ")}>
					{(media_origins_arr.length > 0) && (
						<div>
							<CombinedMediaUploader
								user={user}
								callback={callBackFromSocialNetwork}
								stage={stage}
								media_origins_arr={media_origins_arr}
								accepted_types={accepted_types}
								disabled_add_media={disabled_add_media}
								error_num_files={error_num_files}
								change={change}
								model={state}
								medias={medias}
								errors={errors}
								is_builder={is_builder}
								is_edit={is_edit}
								is_edit_builder={is_edit_builder}
								is_add_builder={is_add_builder}
								total_medias={medias.length}
								intl={props.intl}
								initDropzone={initDropzone}
								fileAdded={fileAdded}
								uploadprogress={uploadprogress}
								setMedias={setMedias}
								completedFile={completedFile}
								addYoutube={addYoutube}
								addText={addText}
								addLink={addLink}
								addFromImgUrl={addFromImgUrl}
								is_main_color_bg_light={isColorBright(getPromotionMainColor(props.promotion))}
								setErrors={setErrors}
								validate={validate}
								promotion={props.promotion}
								pending_files = {stage.media_max_elements -  medias.length}
							/>

							{(medias.length > 0) && (
								<SortableFields
									data={{
										changeMedia,
										deleteFile,
										is_builder,
										is_debug,
										is_edit,
										is_edit_builder,
										is_add_builder,
										stage,
										is_drag_enabled:all_medias_ended,
									}}
									errors={errors}
									intl={props.intl}
									items={medias}
									onSortEnd={onSortEnd}
									useDragHandle={true}
								/>
							)}
						</div>
					)}

					{(media_origins_arr.length == 0 && !text_permitted) && (
						<div>{i18n("There are no file types selected")}</div>
					)}

					{(!is_edit_builder && !is_add_builder) && (
						<div class="template_module mod_align_center register_call_to_action_flex is_react_cta">
							{(!is_builder && has_errors && !submit_enabled) && (
								<div class="form_error_message gui_not_in_bottom_small">
									<strong class="redBg whiteColor original_color_item">{i18n("Please, fix the errors")}</strong>
								</div>
							)}

							{(!is_builder && !has_errors && !all_medias_processed && !is_only_text && !is_only_url) && (
								<div class="form_error_message gui_not_in_bottom_small">
									<strong class="orangeBg whiteColor original_color_item">{i18n("Please wait until all media files are processed")}</strong>
								</div>
							)}

							{(!is_edit) && (
								<div class="finalize_form enter flex_button gui_big_button dis_wo_signin">
									<SaveButton
										color="variable"
										disabled={disabled_submit_step_1}
										_class={{ other:'original_color_item' }}
										onClick={(e, cb) => goToStep(2, cb)}
									>Continue</SaveButton>
								</div>
							)}

							{(!is_edit && media_has_step_0) && (
								<div class="gui_not_in_top_small">
									<ActionLink
										onClick={() => goToStep(0)}
									>Go back</ActionLink>
								</div>
							)}

							{(!is_only_text && !is_edit && (stage.media_min_elements != stage.media_max_elements || stage.media_min_elements > 1)) && (
								<div class="gui_not_in_top_small">
									<p>({i18n("minimum", 'ucfirst')}: {stage.media_min_elements} / {i18n("maximum", 'ucfirst')}: {stage.media_max_elements})</p>
								</div>
							)}
						</div>
					)}
				</div>
			)}

			{(step == 2) && (
				<div class={cont_classes.join(" ")} style={step_2_styles}>
					{(display_cover_fields) && (
						<div>
							{(label_title) && (
								<Input
									errors={errors}
									name="title"
									label_props={{
										mandatory:true,
									}}
									_class={(is_edit || is_edit_builder || is_add_builder) ? { first:true } : null}
									change={change}
									model={state}
									autotranslate={false}
								>{(is_edit_builder || is_add_builder) ? i18n("Title of the cover") : label_title}</Input>
							)}

							{(has_description) && (
								<TextArea
									errors={errors}
									name="description"
									label_props={{
										mandatory:stage?.media_description_mandatory ?? false,
									}}
									change={change}
									model={state}
									autotranslate={false}
									max_length={250}
								>{label_description}</TextArea>
							)}

							{(colors_arr.length > 1) && (
								<div class="react_field type_color_picker">
									<div class={`input custom_field width_100${(!label_title && !has_description) ? ' in_top' : ''}`}>
										<FieldLabel>Pick a color</FieldLabel>

										<ul class="bg_color_picker">
											{colors_arr.map((color, i) => {
												const selected = (state.color == color) ? true : false;
												let li_class = [];
												if(selected) li_class.push('selected');

												const icon_class = (isColorBright(color)) ? 'blackIcon' : 'whiteIcon';

												return (
													<li key={`color_${i}`} class={li_class.join(" ")}>
														<ActionLink onClick={() => change('color', color)} autotranslate={false} style={{backgroundColor:color}}>
															<img src={`${window.cdn_url}img/blank_1x1.png`} width="100%" alt="" />
															<span class={'selected_icon knk-on ' + icon_class} />
														</ActionLink>
													</li>
												)
											})}
										</ul>
									</div>
								</div>
							)}

							{(display_extra_fields) && (
								<DynamicForm
									user={extra_data}
									fileUpload={formFileUpload}
									form={localizeFields((stage?.media_form ?? []), props.locale)}
									onChange={dynamicForm}
									showFormErrors={state.showFormErrors}
									model_classes='gui_not_in_top'
								/>
							)}
						</div>
					)}

					<div class={preview_classes.join(" ")}>
						{(display_real_preview) && (
							<LeveledHTMLText
								_class={{ other:'gui_not_in_bottom_small' }}
								style={{
									'--text-text-align':'center',
									'--text-block-width':'100%',
								}}
							>
								<p>{i18n("Preview your entry")}</p>
							</LeveledHTMLText>
						)}

						<ul class="gui_multimedia_gallery">
							<SingleGallery
								closeLayer={() => {}}
								current_media={0}
								media={uploaded_medias}
								user={{
									Participation:{
										quiz_entry:{
											color:state.color,
											description:(has_description && state.description != "") ? state.description : null,
											media:uploaded_medias,
											title:(label_title && state.title != "") ? state.title : null,
										},
										...user_info,
										...user,
									},
									StageVote:{}
								}}
								stage={{
									...stage,
									voting_user_avatar:(media_has_step_0 === false) ? true : (stage?.media_has_avatar ?? true),
									voting_user_country:stage?.media_has_country ?? true,
								}}
								selected_entry={false}
								promotion={props.promotion}
								is_add_builder={is_add_builder}
								//is_preview={display_real_preview}
								in_registered={true}
								is_builder={is_builder}
							/>
						</ul>

						{(tags.length > 0) && (
							<>
								<LeveledHTMLText
									_class={{ other:(is_builder || is_add_builder) ? 'in_bottom gui_not_in_top' : 'in_bottom gui_not_in_top_big' }}
									style={{
										'--text-text-align':'center',
										'--text-block-width':'100%',
									}}
								>
									<p>{stage?.media_tags_label ?? i18n("Select a tag")}</p>
								</LeveledHTMLText>

								<ToggleCollection
									items={tags.sort((a, b) => {
										if(a.title < b.title) return -1;
										if(a.title > b.title) return 1;
										return 0;
									})}
									onToggle={toggleTag}
									selected={state.tags}
									list_classes={`media_tags_selector gui_actions${(is_builder || is_add_builder) ? '' : ' flex_button'}`}
									icon={false}
									first={true}
								/>
							</>
						)}

						{(display_real_preview) && (
							<div class="gui_not_in_top_small">
								<ActionLink
									onClick={() => goToStep(1)}
									autotranslate={false}
								>{label_to_step1}</ActionLink>
							</div>
						)}
					</div>

					{(display_cover_fields === false && display_extra_fields) && (
						<DynamicForm
							user={extra_data}
							fileUpload={formFileUpload}
							form={localizeFields((stage?.media_form ?? []), props.locale)}
							onChange={dynamicForm}
							showFormErrors={state.showFormErrors}
						/>
					)}

					{(!is_edit && !is_edit_builder && !is_add_builder) && (
						<div class="template_module mod_align_center register_call_to_action_flex is_react_cta">
							{(!is_builder && has_errors && !submit_enabled) && (
								<div class="form_error_message gui_not_in_bottom_small">
									<strong class="redBg whiteColor original_color_item">{i18n("Please, fix the errors")}</strong>
								</div>
							)}

							<div class="finalize_form enter flex_button gui_big_button dis_wo_signin">
								<SaveButton
									color="variable"
									disabled={disabled_submit_step_2}
									_class={{ other:'original_color_item' }}
									onClick={(e, cb) => submitMedias(cb)}
									autotranslate={false}
								>{submit_label}</SaveButton>
							</div>
						</div>
					)}
				</div>
			)}

			{(state.errorFile && !is_edit && !is_edit_builder && !is_add_builder) && (
				<FloatingLayerPublic
					title={`<h1>${i18n("Error file")}</h1>`}
					backButton={() => closeError()}
				>
					<div>
						<p>{`${i18n("Your uploaded file is not correct")}: ${state.errorFile.name}`}</p>
					</div>
				</FloatingLayerPublic>
			)}

			{(state.errorFile && (is_edit || is_edit_builder || is_add_builder)) && (
				<FloatingLayer _class={{size:'small'}} backButton={() => closeError()}>
					<div>
						<SectionTitle>Error file</SectionTitle>

						<p>{`${i18n("Your uploaded file is not correct")}: ${state.errorFile.name}`}</p>
					</div>
				</FloatingLayer>
			)}
		</Wrapper>
	);
}

export default injectIntl(MediaParticipation);